/**
 * Plan Service - API methods for plan/licensing management (Super Admin)
 */

import apiClient from './apiClient';
import type { PlanPricingTier } from '@upskill/shared';

export interface Plan {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  organizationCount?: number;
}

export interface CreatePlanRequest {
  name: string;
  description?: string;
}

export interface UpdatePlanRequest extends Partial<CreatePlanRequest> {
  isActive?: boolean;
}

export interface AssignPlanRequest {
  planId: string | null;
}

export interface ListPlansParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
}

export interface PaginatedPlansResponse {
  data: Plan[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Get paginated list of plans
 */
export const getPlans = async (params?: ListPlansParams): Promise<PaginatedPlansResponse> => {
  const response = await apiClient.get<{ success: boolean } & PaginatedPlansResponse>(
    '/api/v1/admin/plans',
    { params },
  );
  return {
    data: response.data.data,
    meta: response.data.meta,
  };
};

/**
 * Get plan by ID
 */
export const getPlanById = async (id: string): Promise<Plan> => {
  const response = await apiClient.get<{ success: boolean; data: Plan }>(
    `/api/v1/admin/plans/${id}`,
  );
  return response.data.data;
};

/**
 * Create new plan
 */
export const createPlan = async (data: CreatePlanRequest): Promise<Plan> => {
  const response = await apiClient.post<{ success: boolean; data: Plan }>(
    '/api/v1/admin/plans',
    data,
  );
  return response.data.data;
};

/**
 * Update plan
 */
export const updatePlan = async (id: string, data: UpdatePlanRequest): Promise<Plan> => {
  const response = await apiClient.patch<{ success: boolean; data: Plan }>(
    `/api/v1/admin/plans/${id}`,
    data,
  );
  return response.data.data;
};

/**
 * Delete plan
 */
export const deletePlan = async (id: string): Promise<{ message: string }> => {
  const response = await apiClient.delete<{ success: boolean; message: string }>(
    `/api/v1/admin/plans/${id}`,
  );
  return { message: response.data.message };
};

/**
 * Assign (or unassign) a plan to an organization
 */
export const assignPlanToOrg = async (
  orgId: string,
  data: AssignPlanRequest,
): Promise<{ message: string }> => {
  const response = await apiClient.patch<{ success: boolean; message: string }>(
    `/api/v1/admin/plans/organizations/${orgId}/assign`,
    data,
  );
  return { message: response.data.message };
};

/**
 * Get the licensed modules assigned to a plan
 * GET /api/v1/admin/plans/:id/modules
 */
export const getPlanModules = async (planId: string): Promise<string[]> => {
  const response = await apiClient.get<{ success: boolean; data: string[] }>(
    `/api/v1/admin/plans/${planId}/modules`,
  );
  return response.data.data;
};

/**
 * Replace the set of licensed modules for a plan (idempotent)
 * PUT /api/v1/admin/plans/:id/modules
 */
export const setPlanModules = async (
  planId: string,
  modules: string[],
): Promise<string[]> => {
  const response = await apiClient.put<{ success: boolean; data: string[]; message: string }>(
    `/api/v1/admin/plans/${planId}/modules`,
    { modules },
  );
  return response.data.data;
};

/** Shape returned by GET/PUT /api/v1/admin/plans/:id/permissions */
export interface PlanPermissionEntry {
  permissionId: string;
  key: string;
  module: string | null;
  displayName: string;
}

/**
 * Get the fine-grained permission allow-list enabled on a plan.
 * GET /api/v1/admin/plans/:id/permissions
 */
export const getPlanPermissions = async (planId: string): Promise<PlanPermissionEntry[]> => {
  const response = await apiClient.get<{ success: boolean; data: PlanPermissionEntry[] }>(
    `/api/v1/admin/plans/${planId}/permissions`,
  );
  return response.data.data;
};

/**
 * Replace the full fine-grained permission allow-list for a plan (idempotent).
 * PUT /api/v1/admin/plans/:id/permissions
 */
export const setPlanPermissions = async (
  planId: string,
  permissionIds: string[],
): Promise<PlanPermissionEntry[]> => {
  const response = await apiClient.put<{
    success: boolean;
    data: PlanPermissionEntry[];
    message: string;
  }>(`/api/v1/admin/plans/${planId}/permissions`, { permissionIds });
  return response.data.data;
};

/** Tier input for the replace-all endpoint (no id — tiers are recreated server-side). */
export interface PricingTierInput {
  minUsers: number;
  maxUsers: number | null;
  price: number;
  currency: string;
}

/**
 * Get pricing tiers for a plan.
 * GET /api/v1/admin/plans/:id/pricing-tiers
 */
export const getPricingTiers = async (planId: string): Promise<PlanPricingTier[]> => {
  const response = await apiClient.get<{ success: boolean; data: PlanPricingTier[] }>(
    `/api/v1/admin/plans/${planId}/pricing-tiers`,
  );
  return response.data.data;
};

/**
 * Replace the full pricing tier set for a plan (idempotent).
 * PUT /api/v1/admin/plans/:id/pricing-tiers  body: { tiers: [...] }
 */
export const setPricingTiers = async (
  planId: string,
  tiers: PricingTierInput[],
): Promise<PlanPricingTier[]> => {
  const response = await apiClient.put<{ success: boolean; data: PlanPricingTier[] }>(
    `/api/v1/admin/plans/${planId}/pricing-tiers`,
    { tiers },
  );
  return response.data.data;
};

const planService = {
  getPlans,
  getPlanById,
  createPlan,
  updatePlan,
  deletePlan,
  assignPlanToOrg,
  getPlanModules,
  setPlanModules,
  getPlanPermissions,
  setPlanPermissions,
  getPricingTiers,
  setPricingTiers,
};

export default planService;
